/**
 * Calendar Page - Editorial Calendar
 *
 * Content planning and scheduling interface.
 * Shows upcoming content topics and allows scheduling.
 *
 * Features:
 * - Month view calendar with content markers
 * - Topic list with status indicators
 * - Quick add from AI recommendations
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  Sparkles,
  Loader2,
  Lightbulb,
} from 'lucide-react';
import { UserAvatarMenu } from './UserAvatarMenu';

interface CalendarEntry {
  id: string;
  topic_title: string;
  primary_keyword: string;
  scheduled_date: string;
  status: 'planned' | 'generating' | 'generated' | 'published';
  content_intent?: 'informational' | 'commercial' | 'transactional';
  cluster_name?: string;
}

interface Recommendation {
  id: string;
  suggested_title: string;
  suggested_keyword: string;
  recommendation_type: string;
  rationale?: string;
}

interface CalendarPageProps {
  onBack: () => void;
  onGenerateContent: (entryId: string) => void;
  getAuthHeaders?: () => Promise<Record<string, string>>;
  onSettingsClick?: () => void;
  onMFAClick?: () => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const CalendarPage: React.FC<CalendarPageProps> = ({
  onBack,
  onGenerateContent,
  getAuthHeaders,
  onSettingsClick,
  onMFAClick,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchCalendarData();
    fetchRecommendations();
  }, [month, year]);

  const fetchCalendarData = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(
        `/api/plancraft/calendar?year=${year}&month=${month + 1}`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
      // Mock data for demo
      setEntries([
        {
          id: '1',
          topic_title: 'How AI is Revolutionizing Content Marketing',
          primary_keyword: 'AI content marketing',
          scheduled_date: new Date(year, month, 15).toISOString(),
          status: 'planned',
          content_intent: 'informational',
        },
        {
          id: '2',
          topic_title: 'Best SEO Practices for 2024',
          primary_keyword: 'SEO best practices',
          scheduled_date: new Date(year, month, 20).toISOString(),
          status: 'generated',
          content_intent: 'commercial',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch('/api/plancraft/recommendations', { headers });

      if (response.ok) {
        const data = await response.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      // Mock data
      setRecommendations([
        {
          id: 'rec1',
          suggested_title: 'Content Strategy for Small Businesses',
          suggested_keyword: 'content strategy',
          recommendation_type: 'drill_down',
          rationale: 'Based on your interest in content marketing',
        },
      ]);
    }
  };

  const getDaysInMonth = () => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const getEntriesForDate = (day: number) => {
    const dateStr = new Date(year, month, day).toISOString().split('T')[0];
    return entries.filter((e) => e.scheduled_date.split('T')[0] === dateStr);
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleAddEntry = async () => {
    if (!newTopic.trim() || !selectedDate) return;

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch('/api/plancraft/calendar/entry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          topic_title: newTopic,
          primary_keyword: newKeyword || newTopic.split(' ').slice(0, 3).join(' '),
          scheduled_date: selectedDate.toISOString(),
        }),
      });

      if (response.ok) {
        setShowAddModal(false);
        setNewTopic('');
        setNewKeyword('');
        fetchCalendarData();
      }
    } catch (error) {
      console.error('Failed to add entry:', error);
    }
  };

  const handleAddRecommendation = async (rec: Recommendation) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 7); // Schedule a week out

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch('/api/plancraft/calendar/entry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          topic_title: rec.suggested_title,
          primary_keyword: rec.suggested_keyword,
          scheduled_date: tomorrow.toISOString(),
          recommendation_id: rec.id,
        }),
      });

      if (response.ok) {
        // Remove from recommendations
        setRecommendations((prev) => prev.filter((r) => r.id !== rec.id));
        fetchCalendarData();
      }
    } catch (error) {
      console.error('Failed to add recommendation:', error);
    }
  };

  const getStatusColor = (status: CalendarEntry['status']) => {
    switch (status) {
      case 'planned':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'generating':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'generated':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'published':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const days = getDaysInMonth();
  const today = new Date();
  const isToday = (day: number) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-100 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">Planner</h1>
                <p className="text-xs text-gray-500">Editorial Calendar</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedDate(new Date());
                setShowAddModal(true);
              }}
              className="p-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
            <UserAvatarMenu
              onSettingsClick={onSettingsClick}
              onMFAClick={onMFAClick}
            />
          </div>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="bg-white px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <button
            onClick={prevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">
            {MONTHS[month]} {year}
          </h2>
          <button
            onClick={nextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white px-2 py-2">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const dayEntries = getEntriesForDate(day);
            const hasContent = dayEntries.length > 0;

            return (
              <button
                key={day}
                onClick={() => {
                  setSelectedDate(new Date(year, month, day));
                  if (!hasContent) {
                    setShowAddModal(true);
                  }
                }}
                className={`aspect-square flex flex-col items-center justify-center rounded-lg relative transition-colors ${
                  isToday(day)
                    ? 'bg-amber-500 text-white'
                    : selectedDate?.getDate() === day &&
                      selectedDate?.getMonth() === month
                    ? 'bg-amber-100 text-amber-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                <span className={`text-sm ${isToday(day) ? 'font-bold' : ''}`}>
                  {day}
                </span>
                {hasContent && (
                  <div className="flex gap-0.5 mt-0.5">
                    {dayEntries.slice(0, 3).map((entry, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${
                          entry.status === 'published'
                            ? 'bg-purple-500'
                            : entry.status === 'generated'
                            ? 'bg-emerald-500'
                            : 'bg-amber-500'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Upcoming Content */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Upcoming Content
          </h3>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : entries.length > 0 ? (
            <div className="space-y-2">
              {entries.slice(0, 5).map((entry) => (
                <motion.div
                  key={entry.id}
                  layout
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {entry.topic_title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {new Date(entry.scheduled_date).toLocaleDateString()}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(
                            entry.status
                          )}`}
                        >
                          {entry.status}
                        </span>
                      </div>
                    </div>

                    {entry.status === 'planned' && (
                      <button
                        onClick={() => onGenerateContent(entry.id)}
                        className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-200 transition-colors"
                      >
                        Generate
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No content scheduled</p>
              <button
                onClick={() => {
                  setSelectedDate(new Date());
                  setShowAddModal(true);
                }}
                className="mt-3 text-sm text-amber-600 font-medium"
              >
                Add your first topic
              </button>
            </div>
          )}
        </div>

        {/* AI Recommendations */}
        {recommendations.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              AI Suggestions
            </h3>

            <div className="space-y-2">
              {recommendations.map((rec) => (
                <motion.div
                  key={rec.id}
                  layout
                  className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-amber-600 font-medium">
                          {rec.recommendation_type.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900 text-sm">
                        {rec.suggested_title}
                      </p>
                      {rec.rationale && (
                        <p className="text-xs text-gray-500 mt-1">{rec.rationale}</p>
                      )}
                    </div>

                    <button
                      onClick={() => handleAddRecommendation(rec)}
                      className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-200 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Topic Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowAddModal(false);
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full bg-white rounded-t-3xl overflow-hidden pb-[env(safe-area-inset-bottom)]"
            >
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-center">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>

              <div className="p-4 space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Schedule Content</h2>

                {selectedDate && (
                  <p className="text-sm text-gray-500">
                    For {selectedDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Topic</label>
                    <input
                      type="text"
                      value={newTopic}
                      onChange={(e) => setNewTopic(e.target.value)}
                      placeholder="e.g., How to Improve SEO Rankings"
                      className="w-full mt-1 px-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Primary Keyword (optional)
                    </label>
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="e.g., SEO rankings"
                      className="w-full mt-1 px-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddEntry}
                    disabled={!newTopic.trim()}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                      newTopic.trim()
                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Add to Calendar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarPage;
